/**
 * Reports API
 * Operations cost analysis and dashboard endpoints
 */

import apiClient from './client';

// ============================================================
// Types
// ============================================================

export interface CostConfig {
  input_cost_per_token: number;
  output_cost_per_token: number;
  cache_cost_per_token: number;
  exchange_rate: number;
  member_price: number;
  daily_push_cost: number;
  daily_charm_cost: number;
  soulmate_cost: number;
}

export interface UserChatStats {
  user_id: string;
  name: string | null;
  phone: string | null;
  gender: string | null;
  session_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_tokens: number;
  avg_duration: number;
  first_session: string;
  last_session: string;
  lifecycle_days: number;
  chat_cost_cny: number;
  daily_service_cost: number;
  total_cost_cny: number;
}

export interface DailyStats {
  date: string;
  sessions: number;
  users: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  chat_cost_cny: number;
}

export interface SoulmateStats {
  type: 'lifetime' | 'freestyle';
  count: number;
  input_tokens: number;
  output_tokens: number;
  cost_cny: number;
}

export interface TrialAnalysis {
  user_id: string;
  sessions_in_5_days: number;
  lifecycle_days: number;
  retained: boolean;
}

export interface CostSummary {
  total_users: number;
  total_sessions: number;
  total_chat_cost_cny: number;
  total_daily_service_cost_cny: number;
  total_soulmate_cost_cny: number;
  total_cost_cny: number;
  avg_cost_per_user_cny: number;
  median_cost_cny: number;
  cost_percentiles: Record<string, number>;
  profitable_users: number;
  profitable_rate: number;
}

export interface CostTier {
  label: string;
  min_cost: number;
  max_cost: number;
  user_count: number;
  percentage: number;
  avg_cost: number;
  covered_by_membership: boolean;
  profit_per_user: number;
}

export interface PricingScenario {
  id: string;
  name: string;
  price: number;
  daily_service_type: 'full' | 'lite' | 'none';
  refund_rate: number;
  description: string;
  projected_revenue: number;
  projected_cost: number;
  projected_refund_loss: number;
  projected_profit: number;
  profit_margin: number;
}

export interface ReportsDashboard {
  timestamp: string;
  date_range: {
    start: string;
    end: string;
  };
  config: CostConfig;
  summary: CostSummary;
  users: UserChatStats[];
  daily_stats: DailyStats[];
  soulmate_stats: SoulmateStats[];
  trial_analysis: TrialAnalysis[];
  cost_tiers: CostTier[];
  pricing_scenarios: PricingScenario[];
}

// ============================================================
// API Functions
// ============================================================

export const reportsApi = {
  /**
   * Get complete reports dashboard data
   */
  getDashboard: async (startDate?: string, endDate?: string): Promise<ReportsDashboard> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = params.toString() ? `/reports/dashboard?${params}` : '/reports/dashboard';
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get user chat statistics
   */
  getUserStats: async (startDate?: string, endDate?: string): Promise<UserChatStats[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = params.toString() ? `/reports/users?${params}` : '/reports/users';
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get daily usage statistics
   */
  getDailyStats: async (startDate?: string, endDate?: string): Promise<DailyStats[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = params.toString() ? `/reports/daily?${params}` : '/reports/daily';
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get current cost configuration
   */
  getCostConfig: async (): Promise<CostConfig> => {
    const response = await apiClient.get('/reports/config');
    return response.data;
  },

  /**
   * Update cost configuration
   */
  updateCostConfig: async (config: CostConfig): Promise<CostConfig> => {
    const response = await apiClient.post('/reports/config', config);
    return response.data;
  },
};
